/**
 * Input/output contracts for the bulk invitation-email callables.
 *
 * Both callables take a list of invitation IDs scoped to a single event.
 * The Attendees-page UI resolves the current filter visible-rows to a
 * concrete invitationIds list, then calls one or the other depending on
 * whether the organizer wants the system to send the email or to record
 * that they sent it via their own channel.
 *
 * - `sendInvitationEmails` writes mail docs and updates `emailSentAt` /
 *   `emailSendCount` / `emailDeliveryStatus` per invitation.
 * - `markInvitationEmailSentManually` flips the new
 *   `emailMarkedSentManuallyAt` flag without writing any mail.
 */

import { z } from 'zod';

export const sendInvitationEmailsInputSchema = z
  .object({
    eventId: z.string().min(1),
    /**
     * Invitation IDs to send emails for. The callable enforces that every
     * invitation belongs to `eventId`; mismatches are skipped with reason
     * `event_mismatch`.
     */
    invitationIds: z.array(z.string().min(1)).min(1),
  })
  .strict();

export type SendInvitationEmailsInput = z.infer<
  typeof sendInvitationEmailsInputSchema
>;

export interface SendInvitationEmailsResponse {
  success: true;
  /** Number of mail docs written (delivery to SMTP is async). */
  sentCount: number;
  /** Number of invitations where SUPPRESS_EMAILS short-circuited the write. */
  suppressedCount: number;
  /** Per-invitation skip reasons. */
  skipped: Array<{
    invitationId: string;
    reason: 'revoked' | 'in_flight' | 'event_mismatch' | 'not_found';
  }>;
}

export const markInvitationEmailSentManuallyInputSchema = z
  .object({
    eventId: z.string().min(1),
    invitationIds: z.array(z.string().min(1)).min(1),
    /** True to mark, false to clear (organizer correction). */
    marked: z.boolean(),
  })
  .strict();

export type MarkInvitationEmailSentManuallyInput = z.infer<
  typeof markInvitationEmailSentManuallyInputSchema
>;

export interface MarkInvitationEmailSentManuallyResponse {
  success: true;
  updatedCount: number;
  skipped: Array<{
    invitationId: string;
    reason: 'revoked' | 'event_mismatch' | 'not_found';
  }>;
}
